import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    ArrowPathIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    FunnelIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import { useAuth } from "@/features/auth/state/useAuth";

import { formatApprovalLabel, getApprovalQueuePath } from "../approval-utils";
import { getApprovalQueue } from "../api/approvalQueueApi";
import type {
    ApprovalActionType,
    ApprovalQueueFilters,
    ApprovalStatus,
    ApprovalTargetType,
} from "../types";

const STATUS_OPTIONS: Array<{ value: ApprovalStatus | ""; label: string }> = [
    { value: "", label: "All Statuses" },
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "EXECUTION_FAILED", label: "Execution Failed" },
];

const TARGET_OPTIONS: Array<{ value: ApprovalTargetType | ""; label: string }> = [
    { value: "", label: "All Targets" },
    { value: "RIDER", label: "Riders" },
    { value: "DRIVER", label: "Drivers" },
    { value: "ANNOUNCEMENT", label: "Announcements" },
];

const ACTION_OPTIONS: Array<{ value: ApprovalActionType | ""; label: string }> = [
    { value: "", label: "All Actions" },
    { value: "RIDER_SEND_PUSH", label: "Rider Push" },
    { value: "RIDER_SUSPEND", label: "Rider Suspend" },
    { value: "RIDER_REACTIVATE", label: "Rider Reactivate" },
    { value: "RIDER_BAN", label: "Rider Ban" },
    { value: "DRIVER_ACTIVATE", label: "Driver Activate" },
    { value: "DRIVER_DEACTIVATE", label: "Driver Deactivate" },
    { value: "DRIVER_DELETE", label: "Driver Delete" },
    { value: "DRIVER_SEND_PUSH", label: "Driver Push" },
    { value: "DRIVER_SUSPEND", label: "Driver Suspend" },
    { value: "DRIVER_UNSUSPEND", label: "Driver Unsuspend" },
    { value: "DRIVER_BAN", label: "Driver Ban" },
    { value: "ANNOUNCEMENT_SCHEDULE", label: "Announcement Schedule" },
    { value: "ANNOUNCEMENT_SEND_NOW", label: "Announcement Send Now" },
];

const statusPillClassNames: Record<ApprovalStatus, string> = {
    PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    REJECTED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    CANCELLED: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    EXECUTION_FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";

    return new Date(value).toLocaleString("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

export const ApprovalQueueRoute = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "SUPER_ADMIN";
    const [filters, setFilters] = useState<ApprovalQueueFilters>({
        page: 0,
        size: 20,
        status: "PENDING",
        actionType: "",
        targetType: "",
        mine: false,
    });

    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ["approval-queue", filters],
        queryFn: () => getApprovalQueue(filters),
    });

    const requests = data?.data.requests ?? [];
    const pagination = data?.pagination;

    const pendingCount = useMemo(
        () => requests.filter((request) => request.status === "PENDING").length,
        [requests]
    );

    const setFilter = <TKey extends keyof ApprovalQueueFilters>(
        key: TKey,
        value: ApprovalQueueFilters[TKey]
    ) => {
        setFilters((previous) =>
            ({
                ...previous,
                [key]: value,
                ...(key === "page" ? {} : { page: 0 }),
            }) as ApprovalQueueFilters
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300">
                {extractApiError(error)}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                                Approval Queue
                            </h2>
                            <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                                Review protected actions that now require super admin authorization
                                before execution. Regular admins only see their own requests here.
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
                            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                Loaded Requests
                            </p>
                            <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                                {requests.length}
                            </p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
                            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                Pending In View
                            </p>
                            <p className="mt-2 text-2xl font-semibold text-amber-600 dark:text-amber-300">
                                {pendingCount}
                            </p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
                            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                Scope
                            </p>
                            <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {isSuperAdmin
                                    ? filters.mine
                                        ? "Only my requests"
                                        : "All requests"
                                    : "My requests only"}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                        <FunnelIcon className="h-4 w-4" />
                        Queue Filters
                    </div>
                    <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                                Status
                            </span>
                            <select
                                value={filters.status ?? ""}
                                onChange={(event) =>
                                    setFilter("status", event.target.value as ApprovalStatus | "")
                                }
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            >
                                {STATUS_OPTIONS.map((option) => (
                                    <option key={option.label} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                                Target
                            </span>
                            <select
                                value={filters.targetType ?? ""}
                                onChange={(event) =>
                                    setFilter(
                                        "targetType",
                                        event.target.value as ApprovalTargetType | ""
                                    )
                                }
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            >
                                {TARGET_OPTIONS.map((option) => (
                                    <option key={option.label} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                                Action
                            </span>
                            <select
                                value={filters.actionType ?? ""}
                                onChange={(event) =>
                                    setFilter(
                                        "actionType",
                                        event.target.value as ApprovalActionType | ""
                                    )
                                }
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            >
                                {ACTION_OPTIONS.map((option) => (
                                    <option key={option.label} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="flex flex-wrap items-center gap-3 xl:justify-end">
                            {isSuperAdmin ? (
                                <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(filters.mine)}
                                        onChange={(event) =>
                                            setFilter("mine", event.target.checked)
                                        }
                                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    Only my requests
                                </label>
                            ) : null}
                            <Button
                                variant="outline"
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2"
                            >
                                <ArrowPathIcon className="h-4 w-4" />
                                {isFetching ? "Refreshing..." : "Refresh"}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {requests.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                        <ClockIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
                        <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-50">
                            No approval requests found
                        </h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Adjust the filters or refresh the queue to load the latest pending work.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-950/50">
                                    <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                        <th className="px-6 py-4">Request</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4">Initiated By</th>
                                        <th className="px-6 py-4">Created</th>
                                        <th className="px-6 py-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {requests.map((request) => (
                                        <tr
                                            key={request.approvalId}
                                            className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-950/50"
                                        >
                                            <td className="px-6 py-5">
                                                <div className="space-y-2">
                                                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                        {request.summary}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                                                            {formatApprovalLabel(request.targetType)}
                                                        </span>
                                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                                                            {formatApprovalLabel(request.actionType)}
                                                        </span>
                                                        {request.targetDisplayName ? (
                                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                                                                {request.targetDisplayName}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {request.requestReason ? (
                                                        <p className="max-w-xl text-sm text-slate-500 dark:text-slate-400">
                                                            {request.requestReason}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusPillClassNames[request.status]}`}
                                                >
                                                    {formatApprovalLabel(request.status)}
                                                </span>
                                                {request.executionError ? (
                                                    <p className="mt-2 max-w-xs text-xs text-rose-600 dark:text-rose-400">
                                                        {request.executionError}
                                                    </p>
                                                ) : null}
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                                                    <UserCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                                                    <div>
                                                        <p className="font-medium text-slate-900 dark:text-slate-100">
                                                            {request.initiatedBy?.name || "Unknown admin"}
                                                        </p>
                                                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                                            {formatApprovalLabel(request.initiatedBy?.role)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5 text-sm text-slate-500 dark:text-slate-400">
                                                {formatDateTime(request.createdAt)}
                                            </td>
                                            <td className="px-6 py-5 text-right">
                                                <Link
                                                    to={getApprovalQueuePath(request.approvalId)}
                                                    className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                                >
                                                    Review Request
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                Showing page {(pagination?.page ?? 0) + 1} of{" "}
                                {pagination?.totalPages ?? 1}
                            </div>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setFilter("page", Math.max((filters.page ?? 0) - 1, 0))
                                    }
                                    disabled={!pagination?.hasPrevious}
                                    className="flex items-center gap-1.5"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setFilter("page", (filters.page ?? 0) + 1)
                                    }
                                    disabled={!pagination?.hasNext}
                                    className="flex items-center gap-1.5"
                                >
                                    Next
                                    <ChevronRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};
